
import React, { useState, useEffect, useMemo } from 'react';
import { Download, Search, Clock, ChevronRight, ArrowLeft, Calculator, Loader2, Edit, Save, Trash2, AlertCircle, ShieldAlert, Plus } from 'lucide-react';
import { api, Store, Staff, Attendance, calculateDurationMinutes, getJSTDateString, timeToMinutes, CsvColumnConfig, getTrialStatus } from '../api';
import Modal from '../components/ui/Modal';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Input from '../components/ui/Input';
import { useNavigate } from 'react-router-dom';

interface StaffSummary {
    staff: Staff;
    workDays: number;
    totalMinutes: number;
    totalBreakMinutes: number;
    totalSalary: number;
    totalTransportFee: number;
    records: Attendance[];
}

const DEFAULT_CSV_CONFIG: CsvColumnConfig[] = [
    { id: 'date', label: '日付', enabled: true },
    { id: 'staffName', label: 'スタッフ名', enabled: true },
    { id: 'staffId', label: 'スタッフID', enabled: true },
    { id: 'startTime', label: '開始時刻', enabled: true },
    { id: 'endTime', label: '終了時刻', enabled: true },
    { id: 'breakMinutes', label: '休憩（分）', enabled: true },
    { id: 'workHours', label: '実働（時間）', enabled: true },
    { id: 'salary', label: '概算給与', enabled: true },
    { id: 'transportFee', label: '交通費', enabled: true },
    { id: 'employmentType', label: '雇用形態', enabled: true },
    { id: 'note', label: '備考', enabled: true },
];

// 店舗の選択肢「全店舗」の値。
const ALL_STORES = 'all';

const AdminAttendancePage: React.FC = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [stores, setStores] = useState<Store[]>([]);
  const [allStaff, setAllStaff] = useState<Staff[]>([]);
  const [attendanceData, setAttendanceData] = useState<Attendance[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const [targetMonth, setTargetMonth] = useState(new Date().toISOString().slice(0, 7)); 
  const [searchQuery, setSearchQuery] = useState('');

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState<Attendance | null>(null);
  const [isAddMode, setIsAddMode] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const [loadedStores, loadedStaff] = await Promise.all([api.getStores(), api.getStaff()]);
        setStores(loadedStores || []); setAllStaff(loadedStaff || []);
        // 複数店舗を管理している場合は、全店舗の集計から表示する。
        if (loadedStores.length > 0) setSelectedStoreId(loadedStores.length > 1 ? ALL_STORES : loadedStores[0].id);
      } catch (e) { console.error(e); } finally { setIsLoading(false); }
    };
    loadData();
  }, []);

  // 勤怠機能が有効かどうかの判定
  const isAttendanceEnabled = useMemo(() => {
    if (stores.length === 0) return true; // 読み込み中は一旦許可
    const store = stores.find(s => s.id === selectedStoreId) || stores[0];
    const trial = getTrialStatus(store);
    const isTrialActive = !store.stripeSubscriptionId && !trial.isExpired;
    return store.plan === 'business' || isTrialActive;
  }, [stores, selectedStoreId]);

  useEffect(() => {
      if (!selectedStoreId || !isAttendanceEnabled) return;
      const loadMonthlyData = async () => {
          setIsLoading(true);
          try {
              const targetIds = selectedStoreId === ALL_STORES ? stores.map(st => st.id) : [selectedStoreId];
              const att = (await Promise.all(targetIds.map(id => api.getAttendance(id)))).flat();
              setAttendanceData(att.filter(a => a.date.startsWith(targetMonth)));
          } catch(e) { console.error(e); } finally { setIsLoading(false); }
      };
      loadMonthlyData();
  }, [selectedStoreId, targetMonth, isAttendanceEnabled, stores]);

  const handleRowClick = (staffId: string) => {
    setSelectedStaffId(staffId);
    setViewMode('detail');
  };

  const staffSummaries = useMemo(() => {
      const summaries: Record<string, StaffSummary> = {};
      const targetStoreStaff = selectedStoreId === ALL_STORES
          ? allStaff.filter(s => s.stores?.some(id => stores.some(st => st.id === id)))
          : allStaff.filter(s => s.stores?.includes(selectedStoreId));
      
      targetStoreStaff.forEach(staff => {
          const staffRecords = attendanceData.filter(a => a.staffId === staff.id && (a.status === 'finished' || a.status === 'paid_leave'));
          const workDays = new Set(staffRecords.filter(r => r.status === 'finished').map(r => r.date)).size;
          
          let totalWorkMinutes = 0; 
          let totalBreakMinutes = 0;
          staffRecords.forEach(record => {
              if (record.startTime && record.endTime) {
                  totalWorkMinutes += calculateDurationMinutes(record.startTime, record.endTime, record.breakTime);
                  totalBreakMinutes += record.breakTime || 0;
              }
          });

          const totalTransportFee = workDays * (staff.limit || 0);

          summaries[staff.id] = { 
            staff, 
            workDays, 
            totalMinutes: totalWorkMinutes, 
            totalBreakMinutes, 
            totalSalary: Math.round((totalWorkMinutes / 60) * (staff.hourlyWage || 0)),
            totalTransportFee,
            records: staffRecords.sort((a,b) => b.date.localeCompare(a.date)) 
          };
      });
      return Object.values(summaries).filter(s => `${s.staff.lastName} ${s.staff.firstName}`.includes(searchQuery));
  }, [allStaff, attendanceData, selectedStoreId, searchQuery, stores]);

  const selectedSummary = useMemo(() => staffSummaries.find(s => s.staff.id === selectedStaffId), [staffSummaries, selectedStaffId]);

  const daysInMonth = useMemo(() => {
      if (!targetMonth) return [];
      const [year, month] = targetMonth.split('-').map(Number);
      const lastDay = new Date(year, month, 0).getDate();
      return Array.from({ length: lastDay }, (_, i) => `${year}-${String(month).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`);
  }, [targetMonth]);

  const getDayName = (dateStr: string) => ['日','月','火','水','木','金','土'][new Date(dateStr).getDay()];

  const handleOpenAddModal = (date: string) => {
      if (!selectedStaffId) return;
      const newRecord: Attendance = {
          id: '',
          staffId: selectedStaffId,
          storeId: selectedStoreId === ALL_STORES ? (allStaff.find(st => st.id === selectedStaffId)?.stores?.[0] || stores[0]?.id || '') : selectedStoreId,
          date,
          startTime: '',
          endTime: '',
          breakTime: 0,
          status: 'finished',
      };
      setEditingAttendance(newRecord);
      setIsAddMode(true);
      setIsEditModalOpen(true);
  };

  const handleUpdateAttendance = async () => {
      if (!editingAttendance || !currentUser) return;
      setIsLoading(true);
      try {
          const adminProfile = allStaff.find(s => s.id === currentUser.uid);
          const id = isAddMode ? `att_${Date.now()}` : editingAttendance.id;
          const updated: Attendance = {
              ...editingAttendance,
              id,
              updatedAt: new Date().toISOString(),
              updatedBy: currentUser.uid,
              updatedByName: adminProfile ? `${adminProfile.lastName} ${adminProfile.firstName}` : '管理者'
          };
          await api.saveAttendance(updated);
          if (isAddMode) {
              setAttendanceData(prev => [...prev, updated]);
          } else {
              setAttendanceData(prev => prev.map(a => a.id === updated.id ? updated : a));
          }
          setIsEditModalOpen(false);
          setIsAddMode(false);
          showToast(isAddMode ? '勤怠データを追加しました' : '勤怠データを修正しました');
      } catch (e) { showToast('保存に失敗しました。', 'error'); } finally { setIsLoading(false); }
  };

  const handleDeleteAttendance = async () => {
      if (!editingAttendance) return;
      setIsLoading(true);
      try {
          await api.deleteAttendance(editingAttendance.id);
          setAttendanceData(prev => prev.filter(a => a.id !== editingAttendance.id));
          setIsEditModalOpen(false);
          showToast('勤怠データを削除しました。');
      } catch (e) { showToast('削除に失敗しました。', 'error'); } finally { setIsLoading(false); }
  };

  const handleDownloadCSV = () => {
      const activeStore = stores.find(s => s.id === selectedStoreId) || stores[0];
      const csvConfig = activeStore?.csvConfig || DEFAULT_CSV_CONFIG;
      const activeColumns = csvConfig.filter(c => c.enabled);
      const headers = activeColumns.map(c => c.label);
      const rows: (string | number)[][] = [];
      
      staffSummaries.forEach(s => {
          const isSalaried = s.staff.employmentType !== 'part_time';
          s.records.forEach(r => {
              const workMin = r.status === 'paid_leave' ? 0 : calculateDurationMinutes(r.startTime || '', r.endTime || '', r.breakTime);
              rows.push(activeColumns.map(col => {
                  if (col.id.startsWith('custom_')) return col.fixedValue || '';

                  switch (col.id) {
                      case 'date': return r.date;
                      case 'staffName': return `${s.staff.lastName} ${s.staff.firstName}`;
                      case 'staffId': return s.staff.id;
                      case 'startTime': return r.startTime || (r.status === 'paid_leave' ? '有給' : '');
                      case 'endTime': return r.endTime || (r.status === 'paid_leave' ? '有給' : '');
                      case 'breakMinutes': return r.breakTime;
                      case 'workHours': return (workMin / 60).toFixed(2);
                      case 'salary': return isSalaried ? '-' : Math.round((workMin / 60) * s.staff.hourlyWage);
                      case 'transportFee': return r.status === 'finished' ? s.staff.limit : 0;
                      case 'employmentType': return s.staff.employmentType === 'full_time' ? '正社員' : s.staff.employmentType === 'contract' ? '契約社員' : 'アルバイト';
                      case 'note': return r.note || '';
                      default: return '';
                  }
              }));
          });
      });
      const escapeCsvField = (val: string | number) => {
          const s = String(val);
          return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csvContent = "\uFEFF" + [headers.map(escapeCsvField).join(','), ...rows.map(r => r.map(escapeCsvField).join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.setAttribute('href', URL.createObjectURL(blob));
      link.setAttribute('download', `attendance_${targetMonth}.csv`);
      link.click();
  };

  // スタンダードプランでのアクセス制限表示
  if (!isLoading && !isAttendanceEnabled) {
    return (
        <div className="min-h-[60vh] flex flex-col items-center justify-center p-8 bg-white rounded-3xl border border-slate-200 shadow-sm animate-in">
            <div className="w-20 h-20 bg-orange-50 text-orange-500 rounded-full flex items-center justify-center mb-6 border border-orange-100 shadow-inner">
                <ShieldAlert size={40} />
            </div>
            <h2 className="text-xl font-black text-slate-800 mb-2">プラン制限</h2>
            <p className="text-slate-500 text-sm font-bold text-center leading-relaxed mb-8 max-w-sm">
                勤怠管理機能は「ビジネスプラン」専用です。<br/>現在ご利用の「スタンダードプラン」では表示できません。
            </p>
            <button 
                onClick={() => navigate('/admin/settings?tab=general')}
                className="bg-blue-600 text-white font-black px-10 py-4 rounded-2xl shadow-xl shadow-blue-100 hover:bg-blue-700 active:scale-95 transition-all flex items-center gap-2"
            >
                プランを変更する
            </button>
        </div>
    );
  }

  return (
    <div className="animate-in">
      <div className="mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
              <div>
                  <h1 className="text-2xl font-bold text-slate-800">勤怠管理</h1>
              </div>
              <div className="flex flex-wrap gap-2">
                  <select value={selectedStoreId} onChange={(e) => setSelectedStoreId(e.target.value)} className="bg-white border border-slate-300 rounded-[4px] px-3 py-2 text-sm font-bold text-slate-700 outline-none">
                      {stores.length > 1 && <option value={ALL_STORES}>全店舗</option>}
                      {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  <input type="month" value={targetMonth} onChange={e => setTargetMonth(e.target.value)} className="border border-slate-300 p-2 rounded-[4px] text-sm font-bold shadow-sm" />
                  <button onClick={handleDownloadCSV} className="bg-slate-800 text-white px-4 py-2 rounded-[4px] font-bold text-sm flex items-center hover:bg-slate-900 shadow-sm"><Download size={16} className="mr-2" />CSV出力</button>
              </div>
          </div>

          {viewMode === 'list' ? (
              <div className="space-y-4 animate-in">
                  <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} /><input type="text" placeholder="スタッフ名で検索" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-[4px] text-sm shadow-sm outline-none font-bold" /></div>
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                      <table className="w-full text-sm text-left">
                          <thead className="bg-slate-50 text-slate-500 font-bold border-b border-slate-200 text-[10px] uppercase tracking-wider">
                              <tr><th className="p-4">スタッフ</th><th className="p-4 text-center">出勤日数</th><th className="p-4 text-center">実働時間</th><th className="p-4 text-center text-emerald-600">有給</th><th className="p-4 text-right">交通費計</th><th className="p-4 text-right">概算給与</th><th className="p-4"></th></tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                              {isLoading ? <tr><td colSpan={7} className="p-12 text-center text-slate-400"><Loader2 className="animate-spin inline mr-2" size={20}/>読み込み中...</td></tr> : staffSummaries.length === 0 ? <tr><td colSpan={7} className="p-12 text-center text-slate-400 font-bold">該当するデータはありません</td></tr> : staffSummaries.map(s => {
                                  const salaried = s.staff.employmentType !== 'part_time';
                                  const paidLeaveDays = s.records.filter(r => r.status === 'paid_leave').length;
                                  return (
                                      <tr key={s.staff.id} onClick={() => handleRowClick(s.staff.id)} className="hover:bg-slate-50 transition-colors cursor-pointer group">
                                          <td className="p-4"><div className="flex items-center"><div><div className="font-bold text-slate-800 leading-tight">{s.staff.lastName} {s.staff.firstName}</div>{!salaried && <div className="text-[10px] text-slate-400">時給：{s.staff.hourlyWage?.toLocaleString()}円</div>}</div></div></td>
                                          <td className="p-4 text-center font-bold text-slate-600">{s.workDays}日</td>
                                          <td className="p-4 text-center font-bold text-slate-600">{(s.totalMinutes / 60).toFixed(1)}時間</td>
                                          <td className="p-4 text-center text-emerald-600 font-bold">{paidLeaveDays}日</td>
                                          <td className="p-4 text-right font-bold text-slate-500">{s.totalTransportFee.toLocaleString()}円</td>
                                          <td className="p-4 text-right"><span className="text-lg font-bold text-slate-800">{salaried ? '-' : `${s.totalSalary.toLocaleString()}円`}</span></td>
                                          <td className="p-4 text-right text-slate-300 group-hover:text-blue-500 transition-colors"><ChevronRight size={20} /></td>
                                      </tr>
                                  );
                              })}
                          </tbody>
                      </table>
                  </div>
              </div>
          ) : (
              <div className="animate-in">
                  <div className="mb-6 flex flex-wrap items-center gap-x-6 gap-y-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                      <button onClick={() => setViewMode('list')} className="flex items-center text-sm font-bold text-slate-500 hover:text-blue-600 bg-slate-50 px-3 py-2 rounded-[4px] border border-slate-200"><ArrowLeft size={16} className="mr-1.5" />戻る</button>
                      <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-sm font-bold text-slate-700"><span className="text-lg text-slate-900">{selectedSummary?.staff.lastName} {selectedSummary?.staff.firstName}</span><span>有給残：{selectedSummary?.staff.paidLeaveCount}日</span><span>交通費（日額）：{selectedSummary?.staff.limit || 0}円</span><span className="text-blue-600 bg-blue-50 px-3 py-1 rounded-[4px]">当月給与：{selectedSummary?.totalSalary.toLocaleString()}円</span></div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                      <div className="bg-slate-50 p-4 border-b border-slate-200 flex items-center gap-2"><Calculator size={18} className="text-slate-400" /><h3 className="font-bold text-slate-700 text-sm">勤務履歴（日別明細）</h3></div>
                      <table className="w-full text-sm text-left table-fixed">
                          <thead className="bg-white text-slate-400 font-bold border-b border-slate-100 text-[10px] uppercase">
                              <tr><th className="p-4 w-20">日付</th><th className="p-4 w-16">曜日</th><th className="p-4 text-center">業務開始</th><th className="p-4 text-center">業務終了</th><th className="p-4 text-center">休憩</th><th className="p-4 text-center">実働時間</th><th className="p-4 text-center">交通費</th><th className="p-4">備考</th><th className="p-4 w-12"></th></tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50">
                              {daysInMonth.map((date) => {
                                  const dayRecords = selectedSummary?.records.filter(r => r.date === date) || [];
                                  if (dayRecords.length === 0) return (<tr key={date} className="opacity-40 hover:opacity-100 transition-opacity"><td className="p-4 font-bold text-slate-400">{date.split('-')[1]}/{date.split('-')[2]}</td><td className="p-4 font-bold text-slate-400">{getDayName(date)}</td><td colSpan={6} className="p-4 text-center text-[10px] font-bold text-slate-300">記録なし</td><td className="p-4 text-right"><button onClick={() => handleOpenAddModal(date)} className="p-1.5 text-slate-300 hover:text-blue-600 transition-colors" title="記録を追加"><Plus size={16}/></button></td></tr>);
                                  return dayRecords.map((record, idx) => {
                                      const isPaidLeave = record.status === 'paid_leave';
                                      const workMin = isPaidLeave ? 0 : calculateDurationMinutes(record.startTime || '', record.endTime || '', record.breakTime);
                                      const isFirstRow = idx === 0;
                                      return (
                                          <tr key={record.id} className={`hover:bg-slate-50/50 ${isPaidLeave ? 'bg-emerald-50/20' : idx > 0 ? 'bg-blue-50/10' : ''}`}>
                                              <td className="p-4 font-bold text-slate-700">{isFirstRow ? `${record.date.split('-')[1]}/${record.date.split('-')[2]}` : '∟ 同日'}</td>
                                              <td className="p-4 font-bold text-slate-400">{isFirstRow ? getDayName(record.date) : ''}</td>
                                              <td className="p-4 text-center font-mono font-bold text-slate-600">{isPaidLeave ? '-' : record.startTime}</td>
                                              <td className="p-4 text-center font-mono font-bold text-slate-600">{isPaidLeave ? '-' : record.endTime}</td>
                                              <td className="p-4 text-center text-slate-500 font-bold">{isPaidLeave ? '-' : `${record.breakTime}分`}</td>
                                              <td className="p-4 text-center font-bold text-blue-600">{isPaidLeave ? <span className="text-emerald-600">有給</span> : `${(workMin / 60).toFixed(1)}h`}</td>
                                              <td className="p-4 text-center font-bold text-slate-500">{(!isPaidLeave && isFirstRow) ? `${selectedSummary?.staff.limit || 0}円` : '-'}</td>
                                              <td className="p-4 text-[10px] text-slate-400 font-bold truncate">
                                                  {record.note || '-'}
                                                  {record.updatedBy && <div className="text-[8px] text-orange-500 mt-1 flex items-center"><AlertCircle size={8} className="mr-1"/>{record.updatedAt?.split('T')[0]} {record.updatedByName}修正</div>}
                                              </td>
                                              <td className="p-4 text-right"><button onClick={() => { setEditingAttendance(record); setIsEditModalOpen(true); }} className="p-1.5 text-slate-400 hover:text-blue-600 transition-colors"><Edit size={16}/></button></td>
                                          </tr>
                                      );
                                  });
                              })}
                          </tbody>
                      </table>
                  </div>
              </div>
          )}
      </div>

      <Modal isOpen={isEditModalOpen} onClose={() => { setIsEditModalOpen(false); setIsAddMode(false); }} title={isAddMode ? '勤怠データの追加' : '勤怠データの修正'} maxWidth="max-w-md">
          {editingAttendance && (
              <div className="space-y-6 pt-4">
                  <div className="bg-orange-50 p-3 rounded-lg border border-orange-100 flex items-start gap-2 mb-4">
                      <AlertCircle size={18} className="text-orange-500 flex-shrink-0 mt-0.5" /><p className="text-[11px] text-orange-700 font-bold">修正内容は記録されます。スタッフの給与計算に影響するため、正確に入力してください。</p>
                  </div>

                  {/* 種別選択（追加時・既存編集時ともに表示） */}
                  <div>
                      <label className="block text-xs font-bold text-slate-500 mb-2">種別</label>
                      <div className="flex gap-2">
                          <button
                              onClick={() => setEditingAttendance({...editingAttendance, status: 'finished', startTime: editingAttendance.startTime || '', endTime: editingAttendance.endTime || ''})}
                              className={`flex-1 py-2.5 rounded-[4px] text-sm font-bold border transition-all ${editingAttendance.status !== 'paid_leave' ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}
                          >
                              通常勤務
                          </button>
                          <button
                              onClick={() => setEditingAttendance({...editingAttendance, status: 'paid_leave'})}
                              className={`flex-1 py-2.5 rounded-[4px] text-sm font-bold border transition-all ${editingAttendance.status === 'paid_leave' ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}
                          >
                              有給休暇
                          </button>
                      </div>
                  </div>

                  {editingAttendance.status !== 'paid_leave' && (
                      <>
                          <div className="grid grid-cols-2 gap-4">
                              <Input label="業務開始" type="time" value={editingAttendance.startTime} onChange={e => setEditingAttendance({...editingAttendance, startTime: e.target.value})} />
                              <Input label="業務終了" type="time" value={editingAttendance.endTime} onChange={e => setEditingAttendance({...editingAttendance, endTime: e.target.value})} />
                          </div>
                          <Input label="休憩（分）" type="number" value={editingAttendance.breakTime} onChange={e => setEditingAttendance({...editingAttendance, breakTime: Number(e.target.value)})} />
                      </>
                  )}
                  <Input label="備考（修正理由など）" placeholder="入力ミス修正、など" value={editingAttendance.note || ''} onChange={e => setEditingAttendance({...editingAttendance, note: e.target.value})} />

                  <div className="pt-6 flex items-center justify-between border-t border-slate-100">
                      {!isAddMode ? (
                          <button onClick={handleDeleteAttendance} className="flex items-center gap-1.5 text-red-400 hover:text-red-600 font-bold text-sm transition-colors px-2 py-1.5 rounded-[4px] hover:bg-red-50">
                              <Trash2 size={15} />削除
                          </button>
                      ) : (
                          <div />
                      )}
                      <div className="flex gap-3">
                          <button onClick={() => { setIsEditModalOpen(false); setIsAddMode(false); }} className="px-6 py-2.5 text-slate-400 font-bold hover:text-slate-800 transition-colors">キャンセル</button>
                          <button onClick={handleUpdateAttendance} className="px-8 py-2.5 bg-blue-600 text-white font-bold rounded-[4px] shadow-lg flex items-center gap-2 active:scale-95"><Save size={16} /> 保存する</button>
                      </div>
                  </div>
              </div>
          )}
      </Modal>
    </div>
  );
};

export default AdminAttendancePage;
